import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import { 
  Droplets, 
  Target,
  Users,
  Wrench,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  MapPin,
  CheckCircle2
} from 'lucide-react';

const EauAssainissementPage = () => {
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const objectives = [
    'eau.program.objectives.obj1',
    'eau.program.objectives.obj2',
    'eau.program.objectives.obj3',
    'eau.program.objectives.obj4'
  ];

  const actions = [
    'eau.program.actions.action1',
    'eau.program.actions.action2',
    'eau.program.actions.action3',
    'eau.program.actions.action4',
    'eau.program.actions.action5'
  ];

  const indicators = [
    'eau.program.indicators.ind1',
    'eau.program.indicators.ind2',
    'eau.program.indicators.ind3',
    'eau.program.indicators.ind4'
  ];

  const impacts = [
    'eau.program.impact.impact1',
    'eau.program.impact.impact2',
    'eau.program.impact.impact3',
    'eau.program.impact.impact4'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Compact */}
      <section className="relative h-[280px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#07D1EB] via-[#089556] to-[#0ba8c7]">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px)`,
              backgroundSize: '100px 100px'
            }}></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center relative z-10 max-w-4xl mx-auto px-4">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                  <Droplets className="h-4 w-4 text-white" />
                </div>
                <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">
                  {t('eau.program.hero.subtitle')}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-3xl font-bold text-white mb-3 leading-tight">
                {t('eau.program.hero.title')}
              </h1>
              <p className="text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed">
                {t('eau.program.hero.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Context & Objectives - Side by Side with Image */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Image Column */}
          <div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/projet/eau/Image5.jpg"
                alt="Programme eau et assainissement"
                className="w-full h-[400px] object-cover"
                loading="eager"
              />
            </div>
          </div>
          
          {/* Content Column */}
          <div className="space-y-6">
            {/* Context */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#07D1EB] rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                {t('eau.program.context.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed text-[15px]">
                {t('eau.program.context.content')}
              </p>
            </div>

            {/* Objectives */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#089556] rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                {t('eau.program.objectives.title')}
              </h2>
              <ul className="space-y-2">
                {objectives.map((objKey, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#089556] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t(objKey)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Actions - Compact Grid */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center flex items-center justify-center gap-2">
            <Wrench className="w-7 h-7 text-[#07D1EB]" />
            {t('eau.program.actions.title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {actions.map((actionKey, idx) => (
              <div key={idx} className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex items-start gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-[#07D1EB] to-[#089556] rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
                <p className="text-sm text-gray-700 leading-snug pt-1">
                  {t(actionKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Indicators & Partnerships - Two Columns */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Indicators */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#089556]" />
              {t('eau.program.indicators.title')}
            </h2>
            <div className="space-y-3">
              {indicators.map((indKey, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#07D1EB] to-[#089556] rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-800">{t(indKey)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Partnerships */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('eau.program.partnerships.title')}
            </h2>
            <div className="bg-gradient-to-br from-[#07D1EB] to-[#089556] rounded-xl p-6 text-white">
              <p className="text-white/95 leading-relaxed">
                {t('eau.program.partnerships.content')}
              </p>
            </div>
            
            {/* Project Info */}
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                <MapPin className="w-5 h-5 text-[#089556]" />
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Localisation</div>
                  <div className="text-sm font-medium text-gray-800">{t('eau.program.project.location')}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-[#089556]" />
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Statut</div>
                  <div className="text-sm font-medium text-gray-800">{t('eau.program.project.status')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact - With Image */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('eau.program.impact.title')}
              </h2>
              <div className="space-y-3">
                {impacts.map((impactKey, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-white rounded-lg p-4 shadow-sm">
                    <CheckCircle className="w-5 h-5 text-[#089556] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700 leading-snug">
                      {t(impactKey)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="/projet/eau/Image6.jpg"
                  alt="Impact du programme"
                  className="w-full h-64 object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Compact */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-gradient-to-r from-[#07D1EB] to-[#089556] rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            {t('eau.program.cta.title')}
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            {t('eau.program.cta.description')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                navigate(`/${currentLanguage}/contact`);
              }}
              className="inline-flex items-center gap-2 bg-white text-[#089556] px-6 py-3 rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              {t('eau.program.cta.button')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EauAssainissementPage;
